/**
 * Client media-unit detail route — /offers/:offerId/units/:unitId
 *
 * Fetches the offer-scoped client-facing detail endpoint
 * (`mediaUnits.getClientDetail`) and renders the shared `MediaUnitDetailView`
 * with the redacted client payload. Internal-only sections are absent from the
 * payload, so the shared component omits their cards (no availability props are
 * passed). Shows the loading skeleton during fetch and a not-found state on a
 * 404 (the api returns 404 — never 403 — for no-permission / not-in-offer).
 *
 * The back affordance normally returns to the live client view
 * (`/offers/:offerId/client`), but the internal read-only version-preview
 * screen (`/offers/:offerId/versions/:versionId`) also reaches this route via
 * the same "Zobacz szczegóły" action (client-offer-review-shell.tsx). That
 * screen forwards its origin as `?from=version:<versionId>` so the back
 * affordance returns there instead of the live view. CU-86cbde3ae.
 *
 * Traceability: client-media-unit-detail / Client media-unit detail route
 *               renders the shared detail screen.
 */
import * as React from 'react';
import { createFileRoute, useParams, useRouteContext, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { isNotFoundError } from '@ooh/contracts';
import { ClientUnitNotFound } from '../../features/media-units/components/client-unit-not-found';
import { ClientDetailBackLink } from '../../features/media-units/components/client-detail-back-link';
import { offerQueries } from '../../features/offers/api/queries';
import { DetailSkeleton, MediaUnitDetailView } from '@ooh/screens';
import { PageTitleContext } from '../__root';

// ─── Search schema ────────────────────────────────────────────

const ClientUnitDetailSearchSchema = z
  .object({
    /**
     * Origin the client came from, so the back affordance returns there
     * instead of always defaulting to the live client view. Set by the
     * internal version-preview screen as `version:<versionId>`. CU-86cbde3ae.
     */
    from: z.string().optional(),
  })
  .strict();

const VERSION_FROM_PREFIX = 'version:';

/** Extracts the version id from a `from=version:<id>` search param, or null when absent. */
function resolveVersionIdFromSearch(from: string | undefined): string | null {
  if (from == null || !from.startsWith(VERSION_FROM_PREFIX)) return null;
  const versionId = from.slice(VERSION_FROM_PREFIX.length);
  return versionId.length > 0 ? versionId : null;
}

export const Route = createFileRoute('/offers/$offerId/units/$unitId')({
  validateSearch: ClientUnitDetailSearchSchema,
  component: ClientUnitDetailPage,
});

export function ClientUnitDetailPage() {
  const { t } = useTranslation('inventory');
  const { offerId, unitId } = useParams({ from: '/offers/$offerId/units/$unitId' });
  const { from } = useSearch({ from: '/offers/$offerId/units/$unitId' });
  const { auth } = useRouteContext({ from: '__root__' });
  const versionId = resolveVersionIdFromSearch(from);
  const { setPageTitle } = React.useContext(PageTitleContext);

  const { data, isLoading, isError, error } = useQuery(
    offerQueries.unitClientDetail(auth, offerId, unitId),
  );

  if (isLoading) return <DetailSkeleton />;
  if (isError) {
    if (isNotFoundError(error)) return <ClientUnitNotFound offerId={offerId} />;
    throw error;
  }
  if (!data) return null;

  const backLinkTarget =
    versionId != null
      ? ({
          to: '/offers/$offerId/versions/$versionId',
          params: { offerId, versionId },
        } as const)
      : ({ to: '/offers/$offerId/client', params: { offerId } } as const);

  return (
    <MediaUnitDetailView
      unit={data.data}
      canViewCosts={false}
      clientView
      onPageTitleChange={setPageTitle}
      backLink={
        <ClientDetailBackLink
          to={backLinkTarget.to}
          params={backLinkTarget.params}
          label={t('detail.clientDetail.backToOffer')}
        />
      }
    />
  );
}
